import { reportErrorToast } from "@/lib/errorToastAnalytics"
import { useEffect, useRef } from "react"
import { useTheme } from "next-themes"
import { Toaster as Sonner, toast, useSonner } from "sonner"

import { t, useDocumentLanguage } from "@/lib/i18n"
import {
  DEFAULT_TOAST_CLEARANCE,
  useDocumentToastClearance,
  useDocumentToastPosition,
  useIsPhoneForToasts,
  type ToastClearance,
  type ToastPosition,
} from "@/lib/toastPosition"

type ToasterProps = React.ComponentProps<typeof Sonner>

/**
 * Where the stack sits (Sept 29 bug batch, PR A). Top-right with a 64px offset
 * sat exactly on the estimate/invoice builder header bar, so toasts covered
 * "Save draft" and "Save & Attach" — and sonner pauses the timer while the
 * cursor is over the stack, so they hung around. A computer now defaults to
 * bottom-right, 96px up to clear the Lumen bubble; a user can pick the old
 * corner under Settings → Account (ToastPositionSync mirrors it onto <html>).
 * Phones keep the top so toasts never cover the sticky bottom bars on public
 * pages. The corner is shared: whatever else docks there (the Lumen bubble,
 * higher on Messages; the app-update notice) registers its height through
 * lib/toastPosition and the stack starts above it, and while the Lumen panel
 * is open the stack moves to the bottom centre rather than covering it.
 */
export function toastPlacement(
  preferred: ToastPosition,
  isPhone: boolean,
  clearance: ToastClearance = DEFAULT_TOAST_CLEARANCE,
): Pick<ToasterProps, "position" | "offset"> {
  if (isPhone) return { position: "top-center", offset: 64 }
  if (preferred === "top-right") return { position: "top-right", offset: 64 }
  if (clearance.avoidRight) return { position: "bottom-center", offset: { bottom: 24 } }
  return { position: "bottom-right", offset: { bottom: clearance.bottom, right: 24 } }
}

/** Sonner's own English defaults — what the contractor app has always shipped. */
const SONNER_DEFAULT_CONTAINER_LABEL = "Notifications"
const SONNER_DEFAULT_CLOSE_LABEL = "Close toast"

export interface ToasterLabels {
  /** Accessible name of the live region holding the toast stack. */
  container: string
  /** Accessible name of the per-toast close button. */
  close: string
}

const Toaster = ({ labels, ...props }: ToasterProps & { labels?: Partial<ToasterLabels> }) => {
  const { theme = "system" } = useTheme()
  const { toasts } = useSonner()
  useEffect(() => { toasts.forEach(reportErrorToast) }, [toasts])

  // This Toaster is mounted once in App.tsx, ABOVE the router and above every
  // LanguageProvider, so it cannot read the client language from context. It
  // follows the `lang` the provider writes onto <html> instead: on a French
  // client page a toast announces itself in French, and on every contractor
  // screen (<html lang="en">) these resolve to sonner's own English strings,
  // byte for byte. An explicit `labels` prop still wins.
  const lang = useDocumentLanguage()
  const preferred = useDocumentToastPosition()
  const isPhone = useIsPhoneForToasts()
  const clearance = useDocumentToastClearance()
  const placement = toastPlacement(preferred, isPhone, clearance)
  const containerLabel = labels?.container ?? t(lang, "common.notifications")
  const closeLabel = labels?.close ?? t(lang, "common.close_toast")

  // sonner 1.7.4 reads `closeButtonAriaLabel` inside its Toast component but
  // the Toaster never forwards it, so there is no prop to pass — the button
  // is hardcoded to "Close toast". Sync the attribute ourselves, and ONLY
  // when it is not already the English default, so the contractor app runs
  // exactly the code it ran before (no ref work, no observer).
  const sectionRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (closeLabel === SONNER_DEFAULT_CLOSE_LABEL) return
    const root = sectionRef.current
    if (!root || typeof MutationObserver === "undefined") return
    const apply = () => {
      for (const button of root.querySelectorAll("[data-close-button]")) {
        button.setAttribute("aria-label", closeLabel)
      }
    }
    apply()
    const observer = new MutationObserver(apply)
    observer.observe(root, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [closeLabel])

  return (
    <Sonner
      ref={sectionRef}
      theme={theme as ToasterProps["theme"]}
      containerAriaLabel={containerLabel}
      // Position + offset: see toastPlacement above.
      {...placement}
      // Explicit lifetime + a close button. Sonner pauses its timer while the
      // cursor is anywhere over the toast stack, so a toast that spawns under
      // the cursor would hang around "forever". The close button makes any
      // toast dismissible immediately; the shorter duration drains bursts faster.
      duration={3500}
      visibleToasts={4}
      closeButton
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:rounded-lg group-[.toaster]:border-[#ececec] dark:group-[.toaster]:border-[#ffffff26] group-[.toaster]:bg-card/95 group-[.toaster]:text-foreground group-[.toaster]:shadow-panel group-[.toaster]:backdrop-blur-md",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:rounded-md group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:rounded-md group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  )
}

export { Toaster, toast, SONNER_DEFAULT_CONTAINER_LABEL, SONNER_DEFAULT_CLOSE_LABEL }
